/* eslint-disable import/no-cycle -- DevTools provider wires tabs that import this module */
import { resolveAccountId, resolveStoreId } from '@bees-web/core/shared/contexts'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  type FC,
} from 'react'

import { ErrorBoundary } from '@/core/components/ErrorBoundary'
import { TemplateRequestLog, TemplateService } from '@/core/services/templateService'
import { PageProps } from '@/core/types'

import { useFeatureTogglesMonitor } from '../hooks/useFeatureTogglesMonitor'
import { useProcessorMonitor } from '../hooks/useProcessorMonitor'
import {
  DEFAULT_MAX_ACTIONS,
  clearActions,
  useReduxActionsMonitor,
} from '../hooks/useReduxActionsMonitor'
import { useStateMonitor } from '../hooks/useStateMonitor'
import { DevToolsData } from '../types'
import {
  clearDevToolsSessionActive,
  isDevToolsQueryParamEnabled,
  isDevToolsQueryParamExplicitlyDisabled,
  readDevToolsSessionActive,
  writeDevToolsSessionActive,
} from '../utils/queryParamHandler'

import {
  ContextInfoData,
  DevToolAction,
  DevToolContextValue,
  DevToolData,
  DevToolProviderProps,
  TemplateData,
} from './types'

// Code-split the entire debug panel (shell + tabs) out of the `_app` bundle.
// It is only ever mounted for the small subset of sessions with DevTools
// enabled, so it must not be evaluated on every page load in production.
const DebugPanel = dynamic(
  () => import('../components/DebugPanel/DebugPanel').then((m) => m.DebugPanel),
  { ssr: false }
)

const buildContextInfo = (pageProps?: PageProps): ContextInfoData | undefined => {
  if (!pageProps?.context) return undefined

  const { namespace, country, language, page, query, session, tracking } =
    pageProps.context

  const locale = `${language}_${country}`

  return {
    namespace,
    country,
    language,
    locale,
    pageKey: page?.key,
    query,
    accountId: resolveAccountId(session),
    storeId: resolveStoreId(session),
    ajsAnonymousId: tracking?.ajsAnonymousId,
    ajsUserId: tracking?.ajsUserId,
    userInfos: session?.user,
    requestTraceId: tracking?.requestTraceId,
  }
}

const buildTemplateData = (
  templateLogs?: DevToolsData['templateLogs']
): TemplateData | undefined => {
  if (!templateLogs) return undefined

  const ssrLogs: TemplateRequestLog[] = templateLogs.map((log) => ({
    id: log.id,
    templateName: `${log.templateName}`,
    params: log.params,
    cacheKey: log.cacheKey,
    cacheStatus: log.cacheStatus,
    startTime: log.startTime,
    endTime: log.endTime,
    duration: log.duration,
    timestamp: new Date(log.timestamp),
    success: log.success,
    error: log.error ?? undefined,
    data: log.data ?? undefined,
  }))

  return {
    lastUpdated: new Date(),
    requestLogs: ssrLogs,
  }
}

const initialState: DevToolData = {
  reduxActions: {
    actions: [],
    maxActions: DEFAULT_MAX_ACTIONS,
  },
}

function devToolReducer(state: DevToolData, action: DevToolAction): DevToolData {
  switch (action.type) {
    case 'REDUX_STATE':
      return { ...state, state: action.payload }
    case 'TEMPLATE':
      return { ...state, template: action.payload }
    case 'FEATURE_TOGGLES':
      return { ...state, featureFlags: action.payload }
    case 'CONTEXT_INFO':
      return { ...state, contextInfo: action.payload }
    case 'PROCESSOR':
      return { ...state, processor: action.payload }
    case 'REDUX_ACTIONS':
      return { ...state, reduxActions: action.payload }
    case 'ADD_REDUX_ACTION': {
      const currentActions = state.reduxActions?.actions || []
      const maxActions = state.reduxActions?.maxActions || DEFAULT_MAX_ACTIONS
      const newActions = [action.payload, ...currentActions].slice(0, maxActions)
      return {
        ...state,
        reduxActions: {
          actions: newActions,
          maxActions,
        },
      }
    }
    case 'CLEAR_REDUX_ACTIONS':
      return {
        ...state,
        reduxActions: {
          actions: [],
          maxActions: state.reduxActions?.maxActions || DEFAULT_MAX_ACTIONS,
        },
      }
    case 'RESET':
      return initialState
    default:
      return state
  }
}

export const DevToolContext = createContext<DevToolContextValue | null>(null)

export const DevToolProvider: FC<DevToolProviderProps> = ({
  children,
  isEnabled = false,
  pageProps,
}) => {
  const router = useRouter()

  const [devToolData, updateData] = useReducer(devToolReducer, {
    ...initialState,
    contextInfo: buildContextInfo(pageProps),
    template: buildTemplateData(pageProps?.__devToolsData?.templateLogs),
  })
  const [isVisible, setIsVisible] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const [showForTokenEntry, setShowForTokenEntry] = useState(false)

  useEffect(() => {
    if (isEnabled && typeof window !== 'undefined') {
      writeDevToolsSessionActive()
    }
  }, [isEnabled])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    try {
      setIsMounted(true)

      if (isDevToolsQueryParamExplicitlyDisabled()) {
        setShowForTokenEntry(false)
        clearDevToolsSessionActive()
        return
      }

      if (isDevToolsQueryParamEnabled()) {
        setShowForTokenEntry(true)
        writeDevToolsSessionActive()
        return
      }

      if (readDevToolsSessionActive()) {
        setShowForTokenEntry(true)
      }
    } catch (error) {
      console.error('prod', 'Error checking DevTools query param', {
        error: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }, [router.asPath])

  // Hooks are called always, but do nothing when disabled
  useStateMonitor(updateData, isEnabled)
  useProcessorMonitor(updateData, isEnabled)
  useFeatureTogglesMonitor(updateData, isEnabled)
  useReduxActionsMonitor(updateData, isEnabled)

  const clearTemplateLogs = useCallback(() => {
    TemplateService.clearLogs()
    updateData({
      type: 'TEMPLATE',
      payload: {
        lastUpdated: new Date(),
        requestLogs: [],
      },
    })
  }, [])

  const clearReduxActions = useCallback(() => {
    clearActions()
    updateData({ type: 'CLEAR_REDUX_ACTIONS' })
  }, [updateData])

  const contextValue = useMemo<DevToolContextValue>(
    () => ({
      // Direct access to data
      contextInfo: devToolData.contextInfo,
      featureFlags: devToolData.featureFlags,
      template: devToolData.template,
      state: devToolData.state,
      processor: devToolData.processor,
      reduxActions: devToolData.reduxActions,
      // Internal setters
      updateData,
      isVisible,
      setIsVisible,
      isEnabled,
      clearTemplateLogs,
      clearReduxActions,
    }),
    [devToolData, isVisible, isEnabled, clearTemplateLogs, clearReduxActions]
  )

  // Show DebugPanel if enabled OR if query param exists (for token entry)
  // Only render on client side to avoid hydration mismatches
  const shouldShowPanel = isEnabled || (isMounted && showForTokenEntry)

  return (
    <DevToolContext.Provider value={contextValue}>
      {children}
      {shouldShowPanel && isMounted && (
        <ErrorBoundary>
          <DebugPanel />
        </ErrorBoundary>
      )}
    </DevToolContext.Provider>
  )
}

export const useDevTools = (): DevToolContextValue => {
  const context = useContext(DevToolContext)

  if (!context) {
    throw new Error('useDevTools must be used within a DevToolProvider')
  }

  return context
}

export const useDevToolsSafe = (): DevToolContextValue | null => {
  return useContext(DevToolContext)
}

export default DevToolProvider
